import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { validatePassword, PasswordRequirements } from '../utils/validatePassword';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Envelope, ArrowLeft, Check, Key } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email, origin_url: window.location.origin });
      setSent(true);
      toast.success('Link gesendet');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      toast.error(typeof detail === 'string' ? detail : !status ? 'Verbindungsfehler' : `Fehler ${status}`);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen canvas-spain flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#AB1C24] to-[#8A151C] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl font-heading">H</span>
          </div>
          <h1 className="font-heading text-2xl font-bold text-[#292524]">Passwort zurücksetzen</h1>
        </div>
        <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6">
          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-[#57534E] text-sm">
                Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen.
              </p>
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-1">E-Mail</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="deine@email.de"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full bg-[#AB1C24] hover:bg-[#8A151C]"
                data-testid="forgot-submit-btn"
              >
                <Envelope size={18} className="mr-2" />
                {loading ? 'Sende...' : 'Link senden'}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Check size={32} className="text-green-600" weight="bold" />
              </div>
              <p className="text-[#57534E]">
                Wenn ein Konto mit dieser E-Mail existiert, haben wir einen Link gesendet.
              </p>
            </div>
          )}
          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm text-[#AB1C24] hover:underline inline-flex items-center gap-1">
              <ArrowLeft size={14} /> Zurück zum Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const { loginWithTokens } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/auth/reset-password/${token}`).then(r => setInfo(r.data)).catch(() => setInfo({ invalid: true }));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error('Passwörter stimmen nicht überein');
    const pwdErr = validatePassword(password);
    if (pwdErr) return toast.error(pwdErr);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token, password }, { withCredentials: true });
      if (res.data?.user) {
        loginWithTokens(res.data);
        toast.success('Passwort gespeichert — willkommen zurück!');
        navigate('/dashboard', { replace: true });
        return;
      }
      setDone(true);
      toast.success('Passwort zurückgesetzt');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    } finally { setLoading(false); }
  };

  const isMigration = info?.kind === 'google_migration';

  return (
    <div className="min-h-screen canvas-spain flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#AB1C24] to-[#8A151C] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Key size={32} className="text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[#292524]">{isMigration ? 'Passwort festlegen' : 'Neues Passwort'}</h1>
          {info?.name && !info.invalid && (
            <p className="text-sm text-[#57534E] mt-2" data-testid="reset-greeting">
              Hallo {info.name}! {isMigration
                ? 'Dein Konto, dein Fortschritt und dein Abo bleiben genau so, wie sie sind — lege nur noch ein Passwort fest.'
                : `Neues Passwort für ${info.email}.`}
            </p>
          )}
          {info?.invalid && (
            <p className="text-sm text-red-600 mt-2" data-testid="reset-invalid">
              Dieser Link ist ungültig oder abgelaufen. <Link to="/forgot-password" className="underline font-semibold">Neuen Link anfordern</Link>
            </p>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6">
          {!done ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-1">Neues Passwort</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Neues Passwort"
                  required
                  data-testid="new-password-input"
                />
                <PasswordRequirements password={password} />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-1">Passwort bestätigen</label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Passwort wiederholen"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !password || !confirm}
                className="w-full bg-[#AB1C24] hover:bg-[#8A151C]"
                data-testid="reset-submit-btn"
              >
                {loading ? 'Speichere...' : 'Passwort speichern & einloggen'}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Check size={32} className="text-green-600" weight="bold" />
              </div>
              <p className="text-[#57534E]">Dein Passwort wurde erfolgreich geändert.</p>
              <Link to="/login">
                <Button className="w-full bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="back-to-login-btn">
                  Zum Login
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
